import { FC, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { TableContainer } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { ComplianceStatus, ActionCourse } from "src/shared/api-types/certificate";
import type { CourseCertificate, CertificateMap } from "src/api/compliance";
import { formatShortName } from "src/services/formatters";
import courseCategoryService from "src/services/courseCategory";
import CourseStatusTooltip from "src/components/CourseStatusTooltip";
import CourseHistory from "src/features/course-history";
import { NoDataMessage } from "src/components/NoDataMessage";
import { COMMON } from "src/constants";
import { useShoppingCart } from "src/context/ShoppingCartContext";
import { TooltipPinProvider } from "src/context/TooltipPinContext";
import { overviewTableMaxHeightSx } from "src/components/overviewTableStyles";

import "./index.scss";

const StickyTableCell = styled(TableCell)(({ theme }) => ({
  "&.MuiTableCell-head": {
    position: "sticky",
    zIndex: theme.zIndex.appBar - 3,
    width: "200px",
    backgroundColor: "white",
    "&:first-of-type": {
      left: 0,
      zIndex: theme.zIndex.appBar - 1,
    },
  },
  "&.MuiTableCell-body": {
    left: 0,
    backgroundColor: "white",
    position: "sticky",
    zIndex: 2,
  },
}));

// The table scrolls inside its own container so that both header rows stay pinned while scrolling
// (same approach as the aircraft overview table). MUI's `stickyHeader` puts every head cell at
// `top: 0`, so the second header row has to be offset by the height of the first one.
const getTableContainerSx = (secondHeaderRowTop: number) => ({
  position: "relative",
  overflow: "auto",
  ...overviewTableMaxHeightSx,
  "& thead tr:nth-of-type(2) th": { top: `${secondHeaderRowTop}px` },
});

// Types
export interface AircraftType {
  id: number;
  name: string;
}

export interface Course {
  id: string | number;
  fullname: string;
  name: string;
  courseCategory: string;
  roles: Array<{ role_id: number }>;
  // Set on aircraft-specific course columns: the aircraft type whose column group the column belongs to.
  // The same course can be a column in several aircraft groups (courses assigned to an aircraft family).
  aircraftType?: AircraftType;
}

export interface CourseStatus {
  course_id: string | number;
  status: ComplianceStatus;
  validUntil: string | null;
  buffer: string | null;
  days: number | null;
  purchased: boolean;
  purchasableCourse?: ActionCourse;
  courseCertificate?: CourseCertificate;
}

export interface User {
  id: number;
  positions: [string, ...(CourseStatus | null)[]]; // First element is name, rest are course statuses (can be null)
  roles: Array<{ role_id: number }>;
  aircraftTypeIds: number[];
}

interface CrewCourseOverviewTableProps {
  courses?: Course[][];
  users?: User[];
  searchCourses: string[];
  searchRole: number[];
  searchThemes: string[];
  searchStatus: string[];
  searchAircraftTypes: number[];
  includeAircraftCourses: boolean;
  filterPurchased: boolean;
  initialLoadComplete?: boolean;
  certificateMap?: CertificateMap;
  onClearFilters?: () => void;
}

const filterUsersByStatus = (users: User[], searchStatus: string[]): User[] => {
  if (searchStatus.length === 0) return users;

  return users.map((user) => {
    const [firstPosition, ...courseStatuses] = user.positions;
    const filteredStatuses = courseStatuses.map((courseStat) =>
      courseStat && searchStatus.includes(courseStat.status) ? courseStat : ("" as any),
    );

    return {
      ...user,
      positions: [firstPosition, ...filteredStatuses] as [string, ...CourseStatus[]],
    };
  });
};

const filterCoursesByStatus = (courses: Course[][], users: User[]): Course[][] => {
  let statusIndex = 1; //Indexes of status in user positions array 0 index is user name

  // Map courses inside categories array
  return courses.map((courseCategory) => {
    return courseCategory.map((course) => {
      // Check if any user has a status for this course by match index
      const hasUserWithPosition = users.some((user) => user.positions[statusIndex]);

      // Increment status index for next iteration
      statusIndex++;

      // Return course if any user has status exists, empty string if not
      return hasUserWithPosition ? course : ("" as any);
    });
  });
};

const filterCoursesByPurchased = (courses: Course[][], users: User[], filterPurchased: boolean): Course[][] => {
  if (!filterPurchased) return courses;

  // Build column visibility based on whether ANY user has purchasable course
  const columnHasPurchasable: boolean[] = [];
  let statusIndex = 1;

  courses.forEach((courseCategory) => {
    courseCategory.forEach((course) => {
      if (typeof course === "string") {
        columnHasPurchasable.push(false);
      } else {
        const hasPurchasable = users.some((user) => {
          const courseData = user.positions[statusIndex];
          return courseData && (courseData as CourseStatus).purchasableCourse;
        });
        columnHasPurchasable.push(hasPurchasable);
      }
      statusIndex++;
    });
  });

  // Reset and filter courses based on column visibility
  statusIndex = 1;
  return courses.map((courseCategory) => {
    return courseCategory.map((course) => {
      const shouldShow = columnHasPurchasable[statusIndex - 1];
      statusIndex++;
      return shouldShow ? course : ("" as any);
    });
  });
};

const filterUsers = (
  users: User[],
  courseIndexes: Record<number, boolean> | null,
  searchRole: number[],
  searchAircraftTypes: number[],
  filterPurchased: boolean = false,
): User[] => {
  if (searchRole.length > 0) {
    users = users.filter((user) => user.roles.some((role) => searchRole.includes(role.role_id)));
  }

  if (searchAircraftTypes.length > 0) {
    users = users.filter((user) => user.aircraftTypeIds.some((typeId) => searchAircraftTypes.includes(typeId)));
  }

  const filteredUsers = users.map((user) => {
    const [firstPosition, ...courseStatuses] = user.positions;
    let filteredStatuses = courseStatuses.filter((_, index) => courseIndexes?.[index]);

    if (filterPurchased) {
      filteredStatuses = filteredStatuses.map((status) =>
        status !== null && (status as CourseStatus).purchasableCourse ? status : (null as any),
      );
    }

    return {
      ...user,
      positions: [firstPosition, ...filteredStatuses] as [string, ...CourseStatus[]],
    };
  });

  return filteredUsers.filter((user) => {
    const [_, ...courseStatuses] = user.positions;
    return courseStatuses.some(
      (status) =>
        status && (filterPurchased ? (status as CourseStatus).purchasableCourse : (status as CourseStatus).status),
    );
  });
};

// Checked per course, not per column group: an aircraft group mixes courses of several themes.
const filterCoursesByCourseCategory = (course: Course | string, searchThemes: string[]): Course | string => {
  if (typeof course === "string" || searchThemes.length === 0) return course;
  return searchThemes.includes(course.courseCategory || COMMON.UNCATEGORIZED) ? course : "";
};

// Aircraft-specific columns show only when they are switched on, narrowed to the selected aircraft types.
const filterCoursesByAircraftType = (
  course: Course | string,
  includeAircraftCourses: boolean,
  searchAircraftTypes: number[],
): Course | string => {
  if (typeof course === "string" || !course.aircraftType) return course;
  if (!includeAircraftCourses) return "";
  return searchAircraftTypes.length === 0 || searchAircraftTypes.includes(course.aircraftType.id) ? course : "";
};

const filterCoursesByCourseId = (course: Course | string, searchCourses: string[]): Course | string => {
  if (typeof course === "string" || searchCourses.length === 0) return course;
  return searchCourses.includes(String(course.id)) ? course : "";
};

const filterCoursesByRole = (course: Course | string, searchRole: number[]): Course | string => {
  if (typeof course === "string" || searchRole.length === 0) return course;
  return course.roles?.some((role) => searchRole.includes(role.role_id)) ? course : "";
};

interface FilterConfig {
  searchStatus: string[];
  searchThemes: string[];
  searchRole: number[];
  searchCourses: string[];
  searchAircraftTypes: number[];
  includeAircraftCourses: boolean;
  courses: Course[][];
  users: User[];
  filterPurchased: boolean;
}

// The columns and rows on screen; the Excel export uses it too, so the file matches the table
export const applyCourseFilter = ({
  searchStatus,
  searchThemes,
  searchRole,
  searchCourses,
  searchAircraftTypes,
  includeAircraftCourses,
  courses,
  users,
  filterPurchased = false,
}: FilterConfig): { courses: Course[][]; users: User[] } => {
  const courseIndexes: Record<number, boolean> = {};
  // The aircraft type filter is hidden, and ignored, while aircraft courses are switched off
  const activeAircraftTypes = includeAircraftCourses ? searchAircraftTypes : [];

  // Filter courses
  courses = courses.map((courseCategory) =>
    courseCategory.map((course) => {
      let res: Course | string = course;

      // Apply all course filters
      res = filterCoursesByAircraftType(res, includeAircraftCourses, activeAircraftTypes);
      res = filterCoursesByCourseId(res, searchCourses);
      res = filterCoursesByRole(res, searchRole);
      res = filterCoursesByCourseCategory(res, searchThemes);

      return res as Course;
    }),
  );

  // Filter by user status
  if (searchStatus) {
    users = filterUsersByStatus(users, searchStatus);
    courses = filterCoursesByStatus(courses, users);
  }

  // Filter by purchasable
  if (filterPurchased) {
    courses = filterCoursesByPurchased(courses, users, filterPurchased);
  }

  // Build course indexes for user filtering
  let courseIndex = 0;
  courses.forEach((courseCategory) => {
    courseCategory.forEach((course) => {
      if (course && typeof course !== "string") {
        courseIndexes[courseIndex] = true;
      }
      courseIndex++;
    });
  });

  // Filter users based on course visibility
  // If no courses are visible, pass an empty object (not null) so users get filtered correctly
  users = filterUsers(users, courseIndexes, searchRole, activeAircraftTypes, filterPurchased);
  return {
    courses,
    users,
  };
};

// An aircraft column group is labelled with its aircraft type, every other group with its course theme.
// The type name is user data rendered as React text: i18next must not HTML-escape it too ("AS350&#x2F;EC130").
export const getCourseGroupLabel = (course: Course, t: (key: string, options?: Record<string, unknown>) => string) =>
  course.aircraftType
    ? t("crew_course_overview.aircraft_group", {
        name: course.aircraftType.name,
        interpolation: { escapeValue: false },
      })
    : courseCategoryService.getByCode(course.courseCategory)?.getDisplayName(t) || t("no_theme");

const CrewCourseOverviewTable: FC<CrewCourseOverviewTableProps> = ({
  courses: courses_ = [],
  users: users_ = [],
  searchCourses,
  searchRole,
  searchThemes,
  searchStatus,
  searchAircraftTypes,
  includeAircraftCourses,
  filterPurchased,
  initialLoadComplete = false,
  certificateMap,
  onClearFilters,
}) => {
  const { t } = useTranslation();
  const shoppingCart = useShoppingCart();
  const [historyItem, setHistoryItem] = useState<CourseCertificate | null>(null);
  const [historyUserId, setHistoryUserId] = useState<number | null>(null);

  const handleShowHistory = useCallback((certificate: CourseCertificate, userId: number) => {
    setHistoryItem(certificate);
    setHistoryUserId(userId);
  }, []);

  const { courses, users } = useMemo(
    () =>
      applyCourseFilter({
        searchStatus,
        searchThemes,
        searchRole,
        searchCourses,
        searchAircraftTypes,
        includeAircraftCourses,
        courses: courses_,
        users: users_,
        filterPurchased,
      }),
    [
      searchCourses,
      courses_,
      users_,
      searchStatus,
      searchThemes,
      searchRole,
      searchAircraftTypes,
      includeAircraftCourses,
      filterPurchased,
    ],
  );

  const firstHeaderRowRef = useRef<HTMLTableRowElement | null>(null);
  const [firstHeaderRowHeight, setFirstHeaderRowHeight] = useState(0);

  const updateFirstHeaderRowHeight = useCallback(
    () => setFirstHeaderRowHeight(firstHeaderRowRef.current?.offsetHeight ?? 0),
    [],
  );

  useLayoutEffect(() => {
    updateFirstHeaderRowHeight();

    const element = firstHeaderRowRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateFirstHeaderRowHeight);
      return () => window.removeEventListener("resize", updateFirstHeaderRowHeight);
    }

    const observer = new ResizeObserver(updateFirstHeaderRowHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [courses, users, updateFirstHeaderRowHeight]);

  const tableContainerSx = useMemo(() => getTableContainerSx(firstHeaderRowHeight), [firstHeaderRowHeight]);

  const purchaseAction = (courseToAdd: { id: string | number; name: string }, user: User) => {
    shoppingCart.actions.add(courseToAdd as any, user.id);
  };

  // Check if there are any valid courses or users after filtering
  const hasValidCourses = courses.some((cat) => cat.some((c) => c && typeof c !== "string"));
  const hasUsers = users.length > 0;

  // Show "No Data" if initial load has finished and there's nothing to display
  const showNoData = initialLoadComplete && (!hasValidCourses || !hasUsers);

  if (showNoData) {
    return <NoDataMessage onClearFilters={onClearFilters} />;
  }

  return (
    <TooltipPinProvider>
      <TableContainer sx={tableContainerSx}>
        <Table stickyHeader aria-label={String(t("crew_course_overview_table"))} className="crew-table">
          <TableHead>
            <TableRow ref={firstHeaderRowRef}>
              <StickyTableCell className="subcat">
                <span>{t("certificate_type")}</span>
              </StickyTableCell>
              {courses.map((cat, index) => {
                // Filter out empty strings
                const validCourses = cat.filter((c) => c && typeof c !== "string");
                if (validCourses.length === 0) return null;

                return (
                  <StickyTableCell key={index} className={"subcat"} colSpan={validCourses.length}>
                    <span>{getCourseGroupLabel(validCourses[0], t)}</span>
                  </StickyTableCell>
                );
              })}
            </TableRow>
            <TableRow>
              <StickyTableCell className="cell">
                <span>{t("certificates")}</span>
              </StickyTableCell>
              {courses.flatMap((cat, catIndex) =>
                cat
                  .filter((course) => course)
                  .map((course) => (
                    <StickyTableCell key={`${catIndex}-${course.id}`} align="left" className={"rotate-cell cell"}>
                      <p className={"rotate"}>
                        <span className={"inner"} title={`(${course.id}) ${course.fullname || course.name}`}>
                          {course.fullname || course.name}
                        </span>
                      </p>
                    </StickyTableCell>
                  )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <StickyTableCell style={{ whiteSpace: "nowrap" }} className={"cell"} title={user.positions[0]}>
                  {formatShortName(user.positions[0])}
                </StickyTableCell>
                {user.positions.slice(1).map((data, ix) => {
                  const courseStatus = typeof data === "object" && data !== null ? data : null;
                  return (
                    <TableCell
                      key={ix}
                      align="center"
                      padding="none"
                      style={{ verticalAlign: "bottom" }}
                      className="cell"
                    >
                      {courseStatus && (
                        <CourseStatusTooltip
                          courseStatus={courseStatus}
                          userId={user.id}
                          filterPurchased={filterPurchased}
                          onQuickPurchase={
                            courseStatus.purchasableCourse
                              ? () => purchaseAction(courseStatus.purchasableCourse!, user)
                              : undefined
                          }
                          onShowHistory={
                            courseStatus.courseCertificate
                              ? (certificate) => handleShowHistory(certificate, user.id)
                              : undefined
                          }
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {historyItem && historyUserId && certificateMap && (
        <CourseHistory
          userId={historyUserId}
          certificate={historyItem}
          certificateMap={certificateMap}
          onClose={() => {
            setHistoryItem(null);
            setHistoryUserId(null);
          }}
        />
      )}
    </TooltipPinProvider>
  );
};

export default CrewCourseOverviewTable;
